import { useParams } from 'react-router-dom';
import { useBotGet } from '../hooks/use-bot.hook';
import { memo, useMemo } from 'react';
import BotDetailsComponent from '../components/bot-details.component';
import BotControlComponent from '../components/bot-control.component';
import BotLogViewerComponent from '../components/bot-log-viewer.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { Bot } from '../types/bot.types';
import { Job } from '../../jobs/types/job.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDate } from '../../../../../utils/date.util';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import { useSocket } from '../../../../../hooks/use-socket.hook';
import { Tag, Row, Col, Tabs } from 'antd';
import PageTitle from '@components/common/PageTitle';

const BotDetailsContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useBotGet(id || '');

  // Establish socket connection for bot room
  const { status: socketStatus, socket } = useSocket({
    botId: id || '',
    enabled: !!id,
  });

  const prepareDetailsData = useMemo(
    () =>
      (dataObj: Bot | undefined): DetailItem[] => {
        if (!dataObj) return [];

        const detailItems: DetailItem[] = [];
        // prepare data to display in the details card
        Object.entries(dataObj).forEach(([key, value]) => {
          if (
            key === 'tenant' ||
            key === 'logs' ||
            key === 'statusDetails' ||
            key === 'workflowStatus' ||
            key === 'jobId' // we show the job via the 'job' field instead
          ) {
            return;
          }

          if (key === 'job') {
            detailItems.push({
              label: t('bots.job.label', { defaultValue: 'Job' }),
              value: (value as Job)?.jobName ?? '-',
            });
            return;
          }

          if (key === 'createdAt' || key === 'updatedAt') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: formatDate(value),
            });
            return;
          }

          if (key === 'id') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: value.toString(),
            });
            return;
          }
          detailItems.push({
            label: t(`bots.${key}.label`),
            value: String(value),
          });
        });

        return detailItems;
      },
    [t]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.details.title', { entity: t('bots.title') })} />
        <HeaderRow
          title={t('crud.details.title', { entity: t('bots.title') })}
          showBackButton={true}
          trailTarget={id}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  const getSocketStatusTag = () => {
    const statusConfig = {
      connecting: { color: 'processing', text: t('common.socket.connecting') || 'Connecting' },
      connected: { color: 'green', text: t('common.socket.connected') || 'Connected' },
      disconnected: { color: 'default', text: t('common.socket.disconnected') || 'Disconnected' },
      error: { color: 'error', text: t('common.socket.error') || 'Error' },
    };

    const config = statusConfig[socketStatus] || statusConfig.disconnected;
    return (
      <div>
        <Tag color={config.color} style={{ marginBottom: 0 }}>
          {config.text}
        </Tag>
      </div>
    );
  };

  const tabItems = [
    {
      key: 'info',
      label: t('bots.tabs.info') || 'Info',
      children: (
        <BotDetailsComponent
          data={prepareDetailsData(data?.data)}
          success={data?.success ?? false}
          isLoading={isLoading}
          error={error}
        />
      ),
    },
    {
      key: 'controls',
      label: t('bots.tabs.controls') || 'Controls',
      children: (
        <Row gutter={[0, 24]}>
          {id && (
            <Col span={24}>
              <BotControlComponent
                botId={id}
                socket={socket}
                initialStatusDetails={data?.data?.statusDetails}
                initialWorkflowStatus={data?.data?.workflowStatus}
              />
            </Col>
          )}
          {id && (
            <Col span={24}>
              <BotLogViewerComponent botId={id} socket={socket} initialLogs={data?.data?.logs} />
            </Col>
          )}
        </Row>
      ),
    },
  ];

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('bots.title') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('bots.title') })}
        showBackButton={true}
        trailTarget={id}
        customContent={getSocketStatusTag()}
      />
      <div style={{ marginTop: 24 }}>
        <Tabs items={tabItems} />
      </div>
    </>
  );
};

export default WithPermissionCheck(memo(BotDetailsContainer), 'bot.view');
